<template>
  <multiselect
    ref="baseMultiselect"
    :spellcheck="false"
    autocomplete="off"
    :internal-search="false"
    :options="filteredOptions"
    :model-value="modelValue"
    :loading="loading"
    :group-values="groupValues"
    :showNoOptions="showNoOptions"
    :showNoResults="showNoResults"
    :aria-label="ariaLabel"
    :aria-describedby="ariaDescribedby"
    :autofocus="autofocus"
    :label="label"
    :custom-label="customLabel"
    :hide-selected="hideSelected"
    :show-labels="showLabels"
    :close-on-select="clearOnSelect"
    :open-direction="openDirection"
    @search-change="handleSearch"
    @update:modelValue="
      (modelValue, id) => {
        $emit('update:modelValue', modelValue, id)
        updateMultiselectTagSpanWidth()
      }
    "
    @open="
      id => {
        $emit('open', id)
        updateMultiselectTagSpanWidth()
      }
    "
    @close="
      (value, id) => {
        $emit('close', value, id)
        updateMultiselectTagSpanWidth()
      }
    "
    @tag="(searchQuery, id) => $emit('tag', searchQuery, id)"
    @focus-search="focusSearch(value)"
    :class="multiselectCustomStyle"
    :option-height="50"
    :select-label="selectLabel"
    :deselect-label="deselectLabel"
  >
    <template #caret="{ toggle }">
      <span class="arrow position-absolute" @mousedown.prevent.stop="toggle">
        <i v-if="isOpenMultiselect === true" class="mdi mdi-chevron-up cursor-pointer ms-1" />
        <i
          v-else
          class="mdi mdi-chevron-down cursor-pointer ms-1"
          :class="{ 'text-secondary': !$attrs.disabled, 'text-secondary-disable': $attrs.disabled }"
        />
      </span>
    </template>
    <template v-slot:option="props">
      <div v-if="showAddButton" class="w-100 multiselect__option_with_add_button">
        <div class="d-flex justify-content-between">
          {{ props.option.name }}
          <div>
            <i class="mdi mdi-plus-circle text-primary icon"></i>
            <span class="font-16 font-semibold text-primary ms-1 icon">{{ $t('shared.forms.add') }}</span>
          </div>
        </div>
      </div>
      <div v-else-if="baseCompactMultiSelectOption" class="w-100">
        <div class="d-flex justify-content-between">
          {{ props.option.name }}
        </div>
      </div>
    </template>

    <template v-if="$attrs.multiple && baseCompactMultiSelectOption === false" v-slot:selection="{ remove }">
      <div class="multiselect-tags-container d-flex align-items-center">
        <template v-for="(option, index) in visibleTags" :key="index">
          <span class="multiselect__tag">
            <span>{{ customLabel ? customLabel(option) : option[label] || option }}</span>
            <i
              class="multiselect__tag-icon"
              tabindex="1"
              @keydown.prevent.enter="remove(option)"
              @mousedown.prevent="remove(option)"
            ></i>
          </span>
        </template>

        <span v-if="hiddenTagsCount > 0" ref="badgeRef" class="multiselect-hidden-badge"> +{{ hiddenTagsCount }} </span>
      </div>
    </template>

    <template v-if="$attrs.multiple && hideCloseButton !== true" v-slot:afterList>
      <div class="w-100">
        <div class="d-flex justify-content-end">
          <base-button type="button" @click="close" item-class="text-underline pe-2">{{
            $t('shared.forms.close')
          }}</base-button>
        </div>
      </div>
    </template>
    <template v-slot:clear v-if="isDeselectable">
      <div class="multiselect__clear border-0" @mousedown.prevent.stop="() => $emit('deselected')">
        <i class="mdi mdi-close-circle"></i>
      </div>
    </template>
    <template v-if="showDisabledEmptyPlaceholder" v-slot:placeholder>
      <span class="multiselect__placeholder disable-emty-placeholder-color">{{ disabledEmptyPlaceholder }}</span>
    </template>
    <template v-for="(_, slot) of $slots" v-slot:[slot]="scope">
      <slot :name="slot" v-bind="scope" />
      <template v-if="slot === 'afterList' && mobileCloseButton">
        <div
          class="multiselect__option multiselect__option--group d-flex fixed-bottom close-button text-capitalize"
          @click="close"
          :key="slot"
        >
          {{ $t('shared.forms.close') }}
        </div>
      </template>
    </template>
    <template v-if="showNoOptions" v-slot:noOptions>
      <div class="multiselect__option multiselect__option--no-options p-0">
        <span>{{ noOptionsText }}</span>
      </div>
    </template>
    <template v-if="showNoResults" v-slot:noResult>
      <div class="multiselect__option multiselect__option--no-options p-0">
        <span>{{ noResultsText }}</span>
      </div>
    </template>
  </multiselect>
</template>

<script setup>
import { ref, computed, useAttrs, onMounted, onUnmounted, nextTick, watch } from 'vue'

import { useI18n } from 'vue-i18n'

import Multiselect from 'vue-multiselect'

import BaseButton from '@/components/BaseButton.vue'

// #region - attrs set
const attrs = useAttrs()
// endregion

// #region - translate
const { t } = useI18n()
// endregion

// #region - Props
const props = defineProps({
  modelValue: {
    required: true,
    type: [String, Number, Array, Object, null]
  },
  options: {
    required: true,
    type: Array
  },
  groupValues: {
    required: false,
    default: '',
    type: String
  },
  label: {
    required: false,
    default: '',
    type: String
  },
  customLabel: {
    required: false
  },
  deselectable: {
    required: false,
    default: false,
    type: Boolean
  },
  clearOnSelect: {
    required: false,
    default: true,
    type: Boolean
  },
  hideSelected: {
    required: false,
    default: false,
    type: Boolean
  },
  showAddIcon: {
    required: false,
    default: false,
    type: Boolean
  },
  mobileCloseButton: {
    required: false,
    default: false,
    type: Boolean
  },
  ignoreIncludes: {
    required: false,
    type: Boolean
  },
  baseCompactMultiSelectOption: {
    required: false,
    default: false,
    type: Boolean
  },
  openDirection: {
    required: false,
    default: 'bottom',
    type: String
  },
  showNoOptionsText: {
    required: false,
    default: '',
    type: String
  },
  showNoResultsText: {
    required: false,
    default: '',
    type: String
  },
  showNoOptions: {
    required: false,
    default: false,
    type: Boolean
  },
  showNoResults: {
    required: false,
    default: false,
    type: Boolean
  },
  hideCloseButton: {
    required: false,
    default: false,
    type: Boolean
  },
  loading: {
    required: false,
    default: false,
    type: Boolean
  },
  internalSearch: {
    default: true,
    type: Boolean
  },
  disabledEmptyPlaceholder: {
    required: false,
    default: '',
    type: String
  },
  showDisabledEmptyPlaceholder: {
    required: false,
    default: false,
    type: Boolean
  },
  itemClass: {
    type: String,
    required: false,
    default: ''
  },
  selectLabel: {
    required: false,
    default: '',
    type: String
  },
  deselectLabel: {
    required: false,
    default: '',
    type: String
  },
  autofocus: {
    required: false,
    default: false,
    type: Boolean
  },
  showLabels: {
    required: false,
    default: false,
    type: Boolean
  }
})
// endregion

// #region - emit defining
const emit = defineEmits(['open', 'close', 'tag', 'update:modelValue', 'deselected', 'focus-search', 'search-change'])
// endregion

// #region - references
const query = ref('')
const baseMultiselect = ref(null)
const visibleTags = ref([])
const hiddenTagsCount = ref(0)
const badgeRef = ref(null)
// #endregion

// #region - Computed properties
const ariaLabel = computed(() => attrs['aria-label'] || props.label)
const ariaDescribedby = computed(() => attrs['aria-describedby'] || '')

const noOptionsText = computed(() => {
  return props.showNoOptionsText || t('shared.common.empty_list')
})

const noResultsText = computed(() => {
  return props.showNoResultsText || t('shared.common.empty_list')
})

const filteredOptions = computed(() => {
  if (props.internalSearch === false) {
    return props.options ?? []
  }

  if (props.groupValues) {
    return props.options.map(opt => {
      const groupOptions = opt[props.groupValues].filter(groupOpt =>
        includes(getValue(groupOpt, props.label), query.value)
      )
      return {
        ...opt,
        [props.groupValues]: groupOptions
      }
    })
  }
  return props.options?.filter(opt => includes(getValue(opt, props.label), query.value)) ?? []
})

const isOpenMultiselect = computed(() => baseMultiselect.value?.isOpen)

const isDeselectable = computed(() => {
  return (
    props.deselectable &&
    attrs.multiple === false &&
    props.modelValue !== null &&
    props.modelValue !== '' &&
    props.modelValue !== undefined &&
    isOpenMultiselect.value === true
  )
})

const showAddButton = computed(() => {
  const isThereAnOption = Array.isArray(filteredOptions.value) && filteredOptions.value.length

  if (isThereAnOption && attrs.multiple && !attrs.showLabels && props.showAddIcon) {
    return true
  }

  return false
})

const multiselectCustomStyle = computed(() => {
  const variantStyle = props.itemClass || 'multiselect-medium'
  return `${variantStyle} ${query.value?.length > 0 ? 'focus-multiselect' : ''}`
})

// endregion

// #region - Method

const handleSearch = search => {
  query.value = search
  emit('search-change', query.value)
}

const includes = (str, query) => {
  if (str === undefined) str = 'undefined'
  if (str === null) str = 'null'
  if (str === false) str = 'false'
  const text = str.toString().toLocaleLowerCase('tr-TR')
  query = query.toLocaleLowerCase('tr-TR')
  return text.indexOf(query.trim()) !== -1
}

const isEmpty = opt => {
  if (opt === 0) return false
  if (Array.isArray(opt) && opt.length === 0) return true
  return !opt
}

const close = () => {
  baseMultiselect.value.deactivate()
}

const getValue = (option, label) => {
  if (isEmpty(option)) return ''
  if (props.customLabel) return props.customLabel(option)
  return label ? option[label] : option
}

const focusSearch = value => {
  baseMultiselect.value.$refs.search.focus()
  emit('focus-search', value)
}

const getBadgeWidth = () => {
  if (!badgeRef.value) {
    return 0
  }

  const badge = badgeRef.value
  const originalDisplay = badge.style.display
  badge.style.display = 'flex'
  const badgeWidth = badge.getBoundingClientRect().width
  badge.style.display = originalDisplay

  return badgeWidth
}

const updateMultiselectTagSpanWidth = () => {
  nextTick(() => {
    if (baseMultiselect.value && Array.isArray(props.modelValue)) {
      const multiselectElement = baseMultiselect.value.$el
      const width = multiselectElement.offsetWidth
      const tags = props.modelValue

      if (tags.length === 0) {
        visibleTags.value = []
        hiddenTagsCount.value = 0
        return
      }

      let badgeWidth = 0
      if (tags.length > 1) {
        hiddenTagsCount.value = 1
        badgeWidth = getBadgeWidth()
      }

      const arrowSpace = 40
      const usableWidth = width - arrowSpace - (tags.length > 1 ? badgeWidth + 8 : 0) // 8px badge margin

      let spanWidth = '100px'
      let textWidth = '50px'
      let maxVisibleTags = 1

      if (width > 150 && width <= 219) {
        spanWidth = '90px'
        if (props.modelValue.length > 1) {
          textWidth = '40px'
        } else {
          textWidth = '50px'
        }
      } else if (width <= 220) {
        spanWidth = '120px'
        if (props.modelValue.length > 1) {
          textWidth = '60px'
        } else {
          textWidth = '80px'
        }
        maxVisibleTags = 1
      } else if (width <= 300) {
        spanWidth = '140px'
        if (props.modelValue.length > 2) {
          textWidth = '35px'
        } else {
          textWidth = '60px'
        }
        maxVisibleTags = Math.min(2, tags.length)
      } else if (width <= 400) {
        spanWidth = '120px'
        if (props.modelValue.length > 2) {
          textWidth = '50px'
        } else {
          textWidth = '72px'
        }
        maxVisibleTags = Math.min(2, tags.length)
      } else if (width <= 500) {
        spanWidth = '150px'
        if (props.modelValue.length > 3) {
          textWidth = '72px'
        } else {
          textWidth = '90px'
        }
        maxVisibleTags = Math.min(3, tags.length)
      } else {
        spanWidth = '200px'
        textWidth = '120px'
        maxVisibleTags = Math.min(4, tags.length)
      }

      multiselectElement.style.setProperty('--tag-span-width', spanWidth)
      multiselectElement.style.setProperty('--tag-text-width', textWidth)

      if (tags.length > maxVisibleTags) {
        visibleTags.value = tags.slice(0, maxVisibleTags)
        hiddenTagsCount.value = tags.length - maxVisibleTags
      } else {
        visibleTags.value = [...tags]
        hiddenTagsCount.value = 0
      }
    }
  })
}

// #region - Lifecycle hooks
onMounted(() => {
  updateMultiselectTagSpanWidth()
  window.addEventListener('resize', updateMultiselectTagSpanWidth)
})

onUnmounted(() => {
  window.removeEventListener('resize', updateMultiselectTagSpanWidth)
})
// endregion

// #region - watch part
watch(
  () => props.modelValue,
  () => {
    updateMultiselectTagSpanWidth()
  },
  { deep: true }
)

watch(
  () => props.options,
  () => {
    updateMultiselectTagSpanWidth()
  },
  { deep: true }
)
// endregion

// #region - Expose methods
defineExpose({
  focusSearch,
  isOpenMultiselect
})
// endregion
</script>

<style lang="scss" scoped>
.disable-emty-placeholder-color {
  color: $secondary-active;
}

:deep(.multiselect__tag-icon) {
  top: -2px;
}

:deep(.multiselect__single) {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: calc(100% - 40px);
  display: block;
}

:deep(.multiselect__tag) {
  max-width: var(--tag-span-width, 100px);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: inline-block;
  margin-right: 5px;
  flex-shrink: 1; // gerekirse küçülsun diye
}

.multiselect--active .multiselect__tags {
  z-index: 1000;
}

:deep(.multiselect__tag span) {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: var(--tag-text-width, 100px);
  display: inline-block;
}

:deep(.multiselect__placeholder) {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: calc(100% - 40px);
  display: block;
}

:deep(.multiselect__input) {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 100%;
}

:deep(.multiselect--multiple .multiselect__tags-wrap + .multiselect__input) {
  flex: 0 1 auto;
  min-width: 5px;
  max-width: calc(100% - 5px);
}

:deep(.multiselect--multiple .multiselect__tags) {
  overflow: hidden;
}

:deep(.multiselect__tags) {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  padding-right: 35px !important;
  position: relative;
  min-height: 40px;
}

:deep(.multiselect--multiple .multiselect__tags-wrap) {
  display: inline-flex;
  flex-wrap: wrap;
  max-width: calc(100% - 40px);
  flex: 1;
  margin-right: 0;
}

:deep(.arrow) {
  position: absolute !important;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  z-index: 5;
}

.multiselect-hidden-badge {
  background: #eaf6ed;
  border-radius: 12px;
  padding: 4px 14px;
  font-weight: 500;
  font-size: 12px;
  color: $secondary;
  margin-right: 8px;
  display: flex;
  align-items: center;
  height: 21px;
  flex-shrink: 0;
  white-space: nowrap;
}

.multiselect-tags-container {
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  overflow: hidden;
}
</style>
